import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAgency } from '../context/AgencyContext';
import { getAgencyBusinessCall } from '../services/api';
import { format } from 'date-fns';

interface CallLog {
  id: string;
  callerNumber: string;
  calledNumber: string;
  status: string;
  duration?: number;
  recordingUrl?: string;
  extractedData?: {
    callerName?: string;
    callerEmail?: string;
    reason?: string;
    serviceRequested?: string;
    appointmentDate?: string;
    appointmentTime?: string;
    notes?: string;
  };
  conversationHistory?: Array<{
    role: 'user' | 'assistant' | 'system';
    content: string;
    intent?: string;
    confidence?: number;
    timestamp?: { toDate?: () => Date; seconds?: number };
  }>;
  createdAt?: { toDate?: () => Date; seconds?: number };
}

export default function CallDetailPage() {
  const { businessId, callId } = useParams<{ businessId: string; callId: string }>();
  const navigate = useNavigate();
  const { currentAgency, businesses } = useAgency();
  const [call, setCall] = useState<CallLog | null>(null);
  const [loading, setLoading] = useState(true);

  const business = businesses.find(b => b.id === businessId);

  useEffect(() => {
    const fetchCall = async () => {
      if (!currentAgency || !businessId || !callId) return;

      setLoading(true);
      try {
        const data = await getAgencyBusinessCall(currentAgency.id, businessId, callId);
        setCall(data);
      } catch (error) {
        console.error('Error fetching call:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCall();
  }, [currentAgency, businessId, callId]);

  const formatDateTime = (timestamp: CallLog['createdAt']) => {
    if (!timestamp) return 'Unknown';
    if (timestamp.toDate) {
      return format(timestamp.toDate(), 'MMMM d, yyyy h:mm a');
    }
    if (timestamp.seconds) {
      return format(new Date(timestamp.seconds * 1000), 'MMMM d, yyyy h:mm a');
    }
    return 'Unknown';
  };

  const formatTime = (timestamp?: { toDate?: () => Date; seconds?: number }) => {
    if (!timestamp) return '';
    if (timestamp.toDate) {
      return format(timestamp.toDate(), 'h:mm:ss a');
    }
    if (timestamp.seconds) {
      return format(new Date(timestamp.seconds * 1000), 'h:mm:ss a');
    }
    return '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!call) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Call not found</p>
        <Link to={`/clients/${businessId}/calls`} className="text-primary-600 hover:underline mt-2 inline-block">
          Back to calls
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={() => navigate(`/clients/${businessId}/calls`)}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to calls {business && `- ${business.name}`}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Call Info */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Call Information</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-gray-500">Caller Number</dt>
                <dd className="text-sm font-medium text-gray-900">{call.callerNumber}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Date & Time</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {formatDateTime(call.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Duration</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {call.duration
                    ? `${Math.floor(call.duration / 60)}:${String(call.duration % 60).padStart(
                        2,
                        '0'
                      )}`
                    : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Status</dt>
                <dd>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      call.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : call.status === 'in-progress'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {call.status}
                  </span>
                </dd>
              </div>
              {call.recordingUrl && (
                <div>
                  <dt className="text-sm text-gray-500">Recording</dt>
                  <dd className="mt-1">
                    <audio controls className="w-full">
                      <source src={call.recordingUrl} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Extracted Data */}
          <div className="bg-white shadow rounded-lg p-6 mt-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Extracted Information</h2>
            {Object.keys(call.extractedData || {}).length === 0 ? (
              <p className="text-sm text-gray-500">No information extracted</p>
            ) : (
              <dl className="space-y-4">
                {call.extractedData?.callerName && (
                  <div>
                    <dt className="text-sm text-gray-500">Name</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {call.extractedData.callerName}
                    </dd>
                  </div>
                )}
                {call.extractedData?.callerEmail && (
                  <div>
                    <dt className="text-sm text-gray-500">Email</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {call.extractedData.callerEmail}
                    </dd>
                  </div>
                )}
                {call.extractedData?.reason && (
                  <div>
                    <dt className="text-sm text-gray-500">Reason for Call</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {call.extractedData.reason}
                    </dd>
                  </div>
                )}
                {call.extractedData?.serviceRequested && (
                  <div>
                    <dt className="text-sm text-gray-500">Service Requested</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {call.extractedData.serviceRequested}
                    </dd>
                  </div>
                )}
                {call.extractedData?.appointmentDate && (
                  <div>
                    <dt className="text-sm text-gray-500">Requested Appointment</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {call.extractedData.appointmentDate}
                      {call.extractedData.appointmentTime &&
                        ` at ${call.extractedData.appointmentTime}`}
                    </dd>
                  </div>
                )}
                {call.extractedData?.notes && (
                  <div>
                    <dt className="text-sm text-gray-500">Notes</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {call.extractedData.notes}
                    </dd>
                  </div>
                )}
              </dl>
            )}
          </div>
        </div>

        {/* Conversation Transcript */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Conversation Transcript</h2>
            {!call.conversationHistory || call.conversationHistory.length === 0 ? (
              <p className="text-sm text-gray-500">No transcript available</p>
            ) : (
              <div className="space-y-4">
                {call.conversationHistory.map((turn, index) => (
                  <div
                    key={index}
                    className={`flex ${
                      turn.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        turn.role === 'user'
                          ? 'bg-primary-100 text-primary-900'
                          : turn.role === 'assistant'
                          ? 'bg-gray-100 text-gray-900'
                          : 'bg-yellow-50 text-yellow-900'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium uppercase">
                          {turn.role === 'user'
                            ? 'Caller'
                            : turn.role === 'assistant'
                            ? 'AI'
                            : 'System'}
                        </span>
                        {turn.intent && (
                          <span className="text-xs bg-white bg-opacity-50 px-1.5 py-0.5 rounded">
                            {turn.intent}
                          </span>
                        )}
                        {turn.confidence !== undefined && (
                          <span className="text-xs opacity-70">
                            {Math.round(turn.confidence * 100)}%
                          </span>
                        )}
                      </div>
                      <p className="text-sm">{turn.content}</p>
                      {turn.timestamp && (
                        <p className="text-xs opacity-50 mt-1">
                          {formatTime(turn.timestamp)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
